import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Device, DeviceId, DeviceType} from '../../../shared/models/device';
import {DevicesService} from '../../../core/services/devices.service';
import {TilesService} from '../../../core/services/tiles.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {BulkModalService} from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import {DialogBoxButtonColor} from 'src/shared/models/dialog-box';
import {map} from 'rxjs/operators';
import {Option, OptionGroup, OptionGroupMultiSelect} from '../../../shared/models/option';
import {SortTable, SortTableRow} from '../../../shared/models/sort-table/types';
import {BehaviorSubject, Subscription} from 'rxjs';
import {
  SortTableCellButtons,
  SortTableCellKebabMenu,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../shared/models/sort-table/cell-classes';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import {MenuButtonComponent} from '../menu-button/menu-button.component';
import {PillComponent} from '../pill/pill.component';
import {FormsModule} from '@angular/forms';
import {SortTableComponent} from '../sort-table/sort-table.component';
import {isMobileDevice} from '../../../shared/utils/device-utils';
import {LoRaWANDevicePoint, LoRaWANPointFormat, LoRaWANPointType, makeTileFromLoRaWANPoint} from '../../../shared/models/lorawan';
import {TileId} from '../../../shared/models/tile';
import {EMPTY_ID} from 'src/shared/constants/empty-id';
import {TileEditorComponent} from '../tile-editor/tile-editor.component';
import {LoRaWANPointSelectionCallback} from '../../../shared/models/point';
import {LoRaWANSinglePointComponent} from './single-point/lorawan-single-point.component';

const COL_IDX_KEBAB = 0;
const COL_IDX_NAME = 1;
const COL_IDX_BUTTON = 2;
const COL_IDX_DATA_TYPE = 3;
const COL_IDX_DATA_SOURCE = 4;

@Component({
  selector: 'motn-lorawan-points',
  templateUrl: './lorawan-points.component.html',
  styleUrls: ['./lorawan-points.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    PopoverMultiSelectComponent,
    MenuButtonComponent,
    FormsModule,
    SortTableComponent,
    LoRaWANSinglePointComponent,
    TileEditorComponent,
    PillComponent,
  ],
})
export class LoRaWANPointsComponent implements OnInit, OnDestroy {
  @Input() preselectedDevice: DeviceId;
  @Input() useCase: string = '';
  @Input() selectionMode = false;
  @Input() customCallback: LoRaWANPointSelectionCallback;

  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  devices: Device<DeviceType.LoRaWAN>[] = [];
  selectedDevice: DeviceId;
  filteredPoints: LoRaWANDevicePoint[] = [];
  points: LoRaWANDevicePoint[] = [];
  searchTerm: string = '';
  editingPoint: LoRaWANDevicePoint | null | undefined = undefined;
  editingTile: TileId = EMPTY_ID;

  private devicesSub: Subscription;

  deviceMenuOptions: OptionGroupMultiSelect;

  table$: BehaviorSubject<SortTable> = new BehaviorSubject<SortTable>(null);

  constructor(
    private devicesService: DevicesService,
    private tilesService: TilesService,
    private shadeTools: ShadeToolsService,
    private bulkModalService: BulkModalService,
    private store: Store<AppState>,
  ) {
  }


  ngOnInit(): void {
    if (this.preselectedDevice) {
      this.selectedDevice = this.preselectedDevice;
    }

    this.devicesSub = this.store.select('devices').pipe(
      map((state) => {
        return state.devices.filter(d => d.Type === DeviceType.LoRaWAN);
      }),
    ).subscribe(
      (devices) => {
        this.devices = JSON.parse(JSON.stringify(devices)) as Device<DeviceType.LoRaWAN>[];
        this.onDeviceChange();
      },
      () => {
        this.shadeTools.errorDialog();
      },
    );

    this.devicesService.fetchUserDevices$().subscribe();
  }


  ngOnDestroy() {
    this.devicesSub?.unsubscribe();
  }


  getCurrentSelectedDevice(): Device<DeviceType.LoRaWAN> | undefined {
    return this.devices.find(d => d.ID === this.selectedDevice);
  }


  onDeviceMenuClick() {
    const options: Option[] = [];

    for (let device of this.devices) {
      options.push({
        id: '' + device.ID,
        name: device.Name,
        selected: this.selectedDevice === device.ID,
        callback: () => {
          this.selectedDevice = device.ID;
        },
        pill: {
          letter: 'd',
          text: '' + device.ID,
        },
      });
    }

    this.deviceMenuOptions = new OptionGroupMultiSelect(options);
  }


  onDeviceMenuDone() {
    this.deviceMenuOptions = undefined;
  }


  onDeviceChange() {
    if (!this.selectedDevice && this.devices.length) {
      this.selectedDevice = this.devices[0].ID;
    }

    if (!this.selectedDevice) {
      return;
    }

    this.loadPointsForDevice();
  }


  private loadPointsForDevice() {
    const device = this.getCurrentSelectedDevice();
    this.points = device?.Config?.LoRaWAN?.Points ?? [];
    this.filteredPoints = [...this.points];
    this.filterAndRenderPoints();
  }


  configurePoint(point?: LoRaWANDevicePoint) {
    this.editingPoint = point ?? null;
  }


  onPointSaved() {
    this.editingPoint = undefined;
    this.devicesService.fetchUserDevices$().subscribe(() => this.loadPointsForDevice());
  }


  deletePoint(point: LoRaWANDevicePoint) {
    this.devicesService.deleteLoRaWANPoint$(point, this.selectedDevice).subscribe(
      () => this.loadPointsForDevice(),
    );
  }


  makeTile(point: LoRaWANDevicePoint) {
    const tile = makeTileFromLoRaWANPoint(point, this.selectedDevice);
    this.tilesService.createTile$(tile).subscribe({
      next: (tileId) => {
        this.editingTile = tileId;
      },
      error: (err) => {
        const message = err.status === 403
          ? 'You do not have permission to create tiles on this hub or device.'
          : 'Failed to create tile. Please try again.';
        this.shadeTools.errorDialog(message);
      },
    });
  }


  infoDevicesMenu() {
    this.shadeTools.simpleOkDialog(
      'Device Selection',
      'Select a LoRaWAN device to view its available points. Points are extracted from uplink messages received from The Things Stack.',
    );
  }


  filterAndRenderPoints() {
    if (!this.points) {
      return;
    }

    if (this.searchTerm) {
      this.filteredPoints = this.points.filter(p =>
        p.ID[1].toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    } else {
      this.filteredPoints = [...this.points];
    }

    this.renderTable();
  }


  private renderTable() {
    const table: SortTable = {
      css: {
        'min-width': '550px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 25,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    table.columns[COL_IDX_KEBAB] = {
      name: '',
      css: { 'width': '40px' },
    };

    table.columns[COL_IDX_NAME] = {
      name: 'Point Name',
      css: {
        'width': 'calc(100% - 460px)',
      },
      sortingFn: SortTableCellTitleDescription.sortingFnTitle,
    };

    table.columns[COL_IDX_BUTTON] = {
      name: '',
      css: { 'width': '120px' },
    };

    table.columns[COL_IDX_DATA_TYPE] = {
      name: 'Data Type',
      css: {
        'width': '120px',
        'justify-content': 'center',
      },
      sortingFn: SortTableCellTextPill.sortingFnMainText,
    };

    table.columns[COL_IDX_DATA_SOURCE] = {
      name: 'Data Source',
      css: {
        'width': '120px',
        'justify-content': 'center',
      },
      sortingFn: SortTableCellTextPill.sortingFnMainText,
    };

    for (let point of this.filteredPoints) {
      const row: SortTableRow = {
        context: point.ID[1],
        cells: [],
      };

      row.cells[COL_IDX_NAME] = new SortTableCellTitleDescription(point.ID[1], '');

      row.cells[COL_IDX_DATA_TYPE] = new SortTableCellTextPill(
        point.ID[2] === LoRaWANPointFormat.Numeric ? 'num' : 'bool',
        '',
        true
      );

      row.cells[COL_IDX_DATA_SOURCE] = new SortTableCellTextPill(
        point.ID[0] === LoRaWANPointType.UplinkPayload ? 'UP' : 'UM',
        '',
        true
      );

      if (this.selectionMode && this.customCallback) {
        row.cells[COL_IDX_KEBAB] = new SortTableCellTextPill('', '');
        row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([{
          id: 'select',
          text: 'Select',
          colors: ['indigo'],
          callback: () => this.customCallback(point),
        }]);
      } else {
        row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([{
          id: 'make-tile',
          text: 'Make Tile',
          colors: ['indigo'],
          callback: () => this.makeTile(point),
        }]);
        row.cells[COL_IDX_KEBAB] = new SortTableCellKebabMenu(
          JSON.stringify(point.ID),
          [
            {
              id: 'edit',
              name: 'Configure Point',
              callback: () => this.configurePoint(point),
            },
            {
              id: 'delete',
              name: 'Delete Point',
              callback: () => {
                this.shadeTools.startDialog(
                  'Delete LoRaWAN Point',
                  'Are you sure you want to delete this point?',
                  [
                    {
                      text: 'Yes, Delete',
                      callback: () => {
                        this.deletePoint(point);
                        this.shadeTools.closeAll();
                      },
                      color: DialogBoxButtonColor.Red,
                    },
                    {
                      text: 'Cancel',
                      callback: () => {},
                      color: DialogBoxButtonColor.Alt,
                    },
                  ],
                  false, false, false, false,
                );
              },
            },
          ],
          point.ID[1],
          false,
          2,
        );
      }

      table.rows.push(row);
    }

    this.table$.next(table);
  }

  isMobile(): boolean {
    return isMobileDevice();
  }

  createToolsOptions = (): OptionGroup => {
    const options: Option[] = [];

    options.push({
      id: 'bulk-creation',
      name: 'Bulk Tile Creation',
      icon: 'layer-group',
      callback: () => this.openBulkTileCreation(),
    });

    return new OptionGroup(options);
  };

  openBulkTileCreation(): void {
    const deviceId = this.selectedDevice;
    this.bulkModalService.openCreationModal({
      deviceId,
      points: [],
      protocol: DeviceType.LoRaWAN,
    });
  }
}
